import { WorkflowType } from "common/enums/WorkflowType";
import { observer } from "mobx-react-lite";
import React, { useState, useEffect, Fragment, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import { IDelegationUser } from "common/interfaces/IDelegationUser";
import TextField from "@mui/material/TextField";
import { useStores } from "core/stores";
import { IWorkflow } from "common/interfaces/IWorkflow";
import { FaPlus } from "react-icons/fa";
import { IoTrash } from "react-icons/io5";
import "./NewWorkflow.css";
import { IUser } from "common/interfaces/IUser";
import { useSnackbar } from "notistack";
import { IApprovalConfig } from "common/interfaces/IApprovalConfig";
import { ConfirmationModal } from "components/common/ConfirmationModal";

interface NewWorkflowProp {
    children: JSX.Element;
}

export const NewWorkflow = observer((props: NewWorkflowProp) => {
    const snackbar = useSnackbar();
    const { name, type } = useParams();

    const navigate = useNavigate();

    const blankUser: IUser = { userId: 0, name: "" };

    const { workflowStore, userStore } = useStores();

    const [selectedWorkflow, setSelectedWorkflow] = useState<IWorkflow>(workflowStore.selectedWorkflow);

    const [workflowName, setWorkflowName] = useState<string | undefined>(selectedWorkflow.name);

    const [isWorkflowNameValid, setIsWorkflowNameValid] = useState<boolean>(true);

    const [isApproverMinValValid, setIsApproverMinValValid] = useState<boolean>(true);

    const [approverMinValue, setApproverMinValue] = useState<number | undefined>(0);

    const onApproverMinValChange = (e: any) => {
        setApproverMinValue(e.target.value);
    };

    

    const [selectedAccountManager, setSelectedAccountManager] = useState<IUser | undefined>({ name: "", userId: 0 });

    const [isSelectedAccouontManagerValid, setIsSelectedAccouontManagerValid] = useState<boolean>(true);

    const [isSelectedApproverValid, setIsSelectedApproverValid] = useState<boolean>(true);

    const [selectedApprover, setSelectedApprover] = useState<IUser | undefined>({ name: "", userId: 0 });

    const [addedAccountManagers, setAddedAcountManagers] = useState<IUser[]>(
        selectedWorkflow.accountManagers ? (selectedWorkflow.accountManagers as IUser[]) : []
    );

    const [addedApprovalConfig, setAddedAprovalConfig] = useState<IApprovalConfig[]>(
        selectedWorkflow.approvalConfig ? (selectedWorkflow.approvalConfig as IApprovalConfig[]) : []
    );

    const [workflowTypeSelection, setWorkflowTypeSelection] = useState<number | undefined>(selectedWorkflow.workflowType);
    const [isWorkflowTypeValid, setIsWorkflowTypeValid] = useState<boolean>(true);

    const [isLevel2InvestigatorValid, setIsLevel2InvestigatorValid] = useState<boolean>(true);

    useEffect(() => {
        if (name && type) {
            setWorkflowName(name);
            setWorkflowTypeSelection(Number(type));
        }
        setSelectedWorkflow(workflowStore.selectedWorkflow);
    }, []);

    const onWorkflowNameChange = (e: any) => {
        setWorkflowName(e.target.value);
    };

    const handleWorkflowTypeSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setWorkflowTypeSelection(Number(e.target.value));
    };

    const validate = () => {
        let isValid = true;
        isValid = workflowName ? true : false;
        setIsWorkflowNameValid(isValid);
        if (!isValid) return false;
        isValid = workflowTypeSelection ? true : false;
        setIsWorkflowTypeValid(isValid);
        if (!isValid) return false;

        isValid = selectedLevel2Investigator ? true : false;
        setIsLevel2InvestigatorValid(isValid);
        if (!isValid) return false;
        if (workflowTypeSelection == 1 || workflowTypeSelection == 2) {
            isValid = addedAccountManagers.length > 0 ? true : false;
            setIsSelectedAccouontManagerValid(isValid);
        }
        if (!isValid) return false;
        return isValid;
    };

    const onSaveHandle = () => {
        if (validate()) {
            const workflow: IWorkflow = {
                workflowId: selectedWorkflow.workflowId,
                name: workflowName,
                workflowType: Number(workflowTypeSelection),
                level2Investigator: selectedLevel2Investigator,
                accountManagers: addedAccountManagers,
                approvalConfig: addedApprovalConfig,
            };
            if (selectedWorkflow.workflowId) {
                workflowStore.updateWorkflow(workflow).then((response) => {
                    snackbar.enqueueSnackbar(`Workflow ${workflow.name} updated successfully`, {
                        variant: "success",
                    });

                    setTimeout(() => {
                        navigate("/workflows");
                    }, 2000);
                }).catch((error)=> {
                    snackbar.enqueueSnackbar(`Error updating workflow ${workflow.name} `, {
                        variant: "error",
                    });
                });
            } else {
                workflowStore.saveWorkflow(workflow).then((response) => {
                    snackbar.enqueueSnackbar(`Workflow ${workflow.name} created successfully`, {
                        variant: "success",
                    });

                    setTimeout(() => {
                        navigate("/workflows");
                    }, 2000);
                }).catch((error)=> {
                    snackbar.enqueueSnackbar(`Error creating workflow ${workflow.name} `, {
                        variant: "error",
                    });
                });
            }
        }
    };

    const onCloseHandle = () => {
        workflowStore.selectedWorkflow = {};
        navigate("/workflows");
    };

    const [usersList, setUsersList] = useState<IDelegationUser[]>([]);

    useEffect(() => {
        setUsersList(
            userStore.users.map<IDelegationUser>((x) => {
                return { name: x.name as string, userId: x.userId as number };
            })
        );
    }, []);

    const getSelectedLevel2InvestigatorUser = (delegationUserId: number): IUser | undefined => {
        if (userStore.users.length > 0) {
            const delegationUser = userStore.users.filter((x) => x.userId == delegationUserId)[0];
            return { name: delegationUser.name as string, userId: delegationUser.userId as number };
        }
    };

    const [selectedLevel2Investigator, setSelectedLevel2Investigator] = useState<IUser | undefined>(
        selectedWorkflow.level2Investigator ? getSelectedLevel2InvestigatorUser(selectedWorkflow.level2Investigator.userId as number) : undefined
    );

    const onSelectedLevel2InvestigatorChange = (event: any, value: any) => {
        setIsLevel2InvestigatorValid(true);
        setSelectedLevel2Investigator(value);
    };

    const onSelectedAccountManagerChange = (event: any, value: any) => {
        if (value && (value.userId as number) > 0) {
            setSelectedAccountManager({ userId: value.userId, name: value.name });
            setIsSelectedAccouontManagerValid(true);
        } else {
            setSelectedAccountManager(blankUser);
        }
    };

    const onAddAccountMananger = () => {
        if ((selectedAccountManager?.userId as number) > 0) {
            const userAlredyExist = addedAccountManagers.find((x) => x.userId == selectedAccountManager?.userId);
            if (userAlredyExist) {
                snackbar.enqueueSnackbar("Manager already added", {
                    variant: "warning",
                });
                return;
            }
            setAddedAcountManagers([...addedAccountManagers, selectedAccountManager as IUser]);
            setIsSelectedAccouontManagerValid(true);
        } else {
            setIsSelectedAccouontManagerValid(false);
        }
    };

    const onRemoveAccountMananger = (userId: number) => {
        const newAccountManagers = addedAccountManagers.filter((x) => x.userId != userId);
        setAddedAcountManagers(newAccountManagers);
    };

    const onSelectedApproverChange = (event: any, value: IUser) => {
        if (value && (value.userId as number) > 0) {
            setSelectedApprover({ userId: value.userId, name: value.name });
            setIsSelectedApproverValid(true);
        } else {
            setSelectedApprover(blankUser);
        }
    };

    const onAddApprover = () => {
        if ((selectedApprover?.userId as number) > 0) {
            const userAlredyExist = addedApprovalConfig.find((x) => x.approver?.userId == selectedApprover?.userId);
            if (userAlredyExist) {
                snackbar.enqueueSnackbar("Approver already added", {
                    variant: "warning",
                });
                return;
            }
            if ((approverMinValue as number) > 0) {
                setIsApproverMinValValid(true);
            } else {
                setIsApproverMinValValid(false);
                return;
            }
            setAddedAprovalConfig([...addedApprovalConfig, { approver: selectedApprover, minValue: approverMinValue?.toLocaleString() }]);
            setIsSelectedApproverValid(true);
        } else {
            setIsSelectedApproverValid(false);
            setIsApproverMinValValid((approverMinValue as number) > 0 ? true : false);
        }
    };

    const onRemoveApprover = (userId: number) => {
        const newApprovers = addedApprovalConfig.filter((x) => x.approver?.userId != userId);
        setAddedAprovalConfig(newApprovers);
    };

    const [showDeleteWorkflowConfirmation, setShowDeleteWorkflowConfirmation] = useState<boolean>(false);

    const onDeleteWorkflow = () => {
        setShowDeleteWorkflowConfirmation(true);
    };

    const onConfirmationYes = () => {
        workflowStore
            .deletetWorkflowById(selectedWorkflow.workflowId as number)
            .then((response) => {
                snackbar.enqueueSnackbar("Workflow deleted", {
                    variant: "success",
                });
                setShowDeleteWorkflowConfirmation(false);
                setTimeout(() => {
                    navigate("/workflows");
                }, 2000);
            })
            .catch((error) => {
                snackbar.enqueueSnackbar("Error deleting workflow", {
                    variant: "error",
                });
                setShowDeleteWorkflowConfirmation(false);
            });
    };

    const onConfirmationCancel = () => {
        setShowDeleteWorkflowConfirmation(false);
    };

    return (
        <>
            <div className="container-fluid p-1">
                <div className="m-4 card">
                    <div className="card-header">
                        <div className="row">
                            <div className="col-6 align-content-center">
                                <h1>Workflow Configuration</h1>
                            </div>
                        </div>
                    </div>
                    <div className="row card-body w-100 h-100">
                        <form className="needs-validation">
                            <div className="form-group row pb-2">
                                <label htmlFor="name" className="col-md-2 col-form-label">
                                    Worflow Name
                                </label>
                                <div className="col-md-4">
                                    <input
                                        type="name"
                                        name="name"
                                        required
                                        value={workflowName}
                                        onChange={onWorkflowNameChange}
                                        className={isWorkflowNameValid ? "form-control" : "form-control is-invalid"}
                                        id="inputName"
                                        placeholder="Name"
                                    />
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="name" className="col-md-2 col-form-label">
                                    Workflow Type
                                </label>
                                <div className="col-md-4">
                                    <select
                                        value={workflowTypeSelection}
                                        required
                                        onChange={(e) => handleWorkflowTypeSelectionChange(e)}
                                        className={isWorkflowTypeValid ? "form-control" : "form-control is-invalid"}
                                        name="userType"
                                    >
                                        <option selected={true} value={""}>
                                            Select Workflow Type
                                        </option>
                                        <option value={"1"}>Price Claim</option>
                                        <option value={"2"}>Inventory/Uplift/RMA</option>
                                        <option value={"3"}>Ad hoc</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="userType" className="col-md-2 col-form-label">
                                    {workflowTypeSelection == 3 ? "Finance Manager" : "Investigator"}
                                </label>
                                <div className="col-md-4">
                                    <Autocomplete
                                        disablePortal
                                        title={"Level 2 Investigator"}
                                        value={selectedLevel2Investigator}
                                        onChange={(event, value) => onSelectedLevel2InvestigatorChange(event, value)}
                                        renderInput={(params) => <TextField error={!isLevel2InvestigatorValid} {...params} />}
                                        options={usersList}
                                        componentsProps={{ popper: { style: { width: "fit-content", height: "100px" } } }}
                                        getOptionKey={(option: IUser) => option.userId as number}
                                        getOptionLabel={(option: IUser) => option.name as string}
                                    />
                                </div>
                            </div>
                            {(workflowTypeSelection == 1 || workflowTypeSelection == 2) && (
                                <div className="form-group row pb-2">
                                    <label htmlFor="userType" className="col-md-2 col-form-label">
                                        Account Managers
                                    </label>
                                    <div className="col-md-4">
                                        {addedAccountManagers &&
                                            addedAccountManagers.map((x) => {
                                                return (
                                                    <div className="row pt-1">
                                                        <div className="col-6">{x.name}</div>
                                                        <div className="col-4"></div>
                                                        <div className="col-2">
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => onRemoveAccountMananger(x.userId as number)}
                                                                type="button"
                                                            >
                                                                <IoTrash className="button-icons-size" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        <div className="row pt-1">
                                            <div className="col-6">
                                                <Autocomplete
                                                    disablePortal
                                                    title={"Account Manager"}
                                                    value={selectedAccountManager}
                                                    onChange={(event, value) => onSelectedAccountManagerChange(event, value)}
                                                    renderInput={(params) => <TextField error={!isSelectedAccouontManagerValid} {...params} />}
                                                    options={usersList}
                                                    componentsProps={{ popper: { style: { width: "fit-content", height: "100px" } } }}
                                                    getOptionKey={(option: IUser) => option.userId as number}
                                                    getOptionLabel={(option: IUser) => option.name as string}
                                                />
                                            </div>
                                            <div className="col-4"></div>
                                            <div className="col-2">
                                                <button className="btn btn-outline-secondary btn-sm" onClick={onAddAccountMananger} type="button">
                                                    <FaPlus className="button-icons-size" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="form-group row pb-2">
                                <label htmlFor="userType" className="col-md-2 col-form-label">
                                    Approval Configuration
                                </label>
                                <div className="col-md-4">
                                    <div className="row pt-1">
                                        <div className="col-6">
                                            <span className="col-form-label badge badge-secondary">Approver</span>
                                        </div>
                                        <div className="col-4 text-right">
                                            {" "}
                                            <span className="col-form-label badge badge-secondary">Min. Value</span>
                                        </div>
                                        <div className="col-2"></div>
                                    </div>
                                    {addedApprovalConfig &&
                                        addedApprovalConfig.map((x) => {
                                            return (
                                                <div className="row pt-1">
                                                    <div className="col-6">{x.approver?.name}</div>
                                                    <div className="col-4">{x.minValue?.toLocaleString()}</div>
                                                    <div className="col-2">
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => onRemoveApprover(x.approver?.userId as number)}
                                                            type="button"
                                                        >
                                                            <IoTrash className="button-icons-size" />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    <div className="row pt-1">
                                        <div className="col-6">
                                            <Autocomplete
                                                disablePortal
                                                title={"Approver"}
                                                value={selectedApprover as IUser}
                                                onChange={(event, value) => onSelectedApproverChange(event, value as IUser)}
                                                renderInput={(params) => <TextField error={!isSelectedApproverValid} {...params} />}
                                                options={usersList}
                                                componentsProps={{ popper: { style: { width: "fit-content", height: "100px" } } }}
                                                getOptionKey={(option: IUser) => option.userId as number}
                                                getOptionLabel={(option: IUser) => option.name as string}
                                            />
                                        </div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                name="minVal"
                                                required
                                                value={approverMinValue}
                                                onChange={onApproverMinValChange}
                                                className={isApproverMinValValid ? "form-control" : "form-control is-invalid"}
                                                id="inputMinVal"
                                                placeholder="Min Val"
                                            />
                                        </div>
                                        <div className="col-2">
                                            <button className="btn btn-outline-secondary btn-sm" onClick={onAddApprover} type="button">
                                                <FaPlus className="button-icons-size" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="form-group row button-row-margin">
                                <div className="col-md-12">
                                    <button className="btn btn-primary" onClick={onSaveHandle} type="button">
                                        Save
                                    </button>
                                    <a className="btn btn-primary ms-1" onClick={onCloseHandle}>
                                        Close
                                    </a>
                                    <button
                                        type="button"
                                        onClick={onDeleteWorkflow}
                                        data-toggle="modal"
                                        data-target="#exampleModal"
                                        className="btn btn-primary float-end d-inline"
                                    >
                                        Delete Workflow
                                    </button>

                                    {showDeleteWorkflowConfirmation && (
                                        <ConfirmationModal
                                            onYesClick={onConfirmationYes}
                                            onCancelClick={onConfirmationCancel}
                                            modalText="Are you sure you want to delete the workflow?"
                                            modalTitle="Delete Workflow"
                                        />
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
});
